import {
    Converter,
    convertUsing,
    forMember,
    mapFrom,
    MappingConfiguration,
} from '@automapper/core';
import {
    EntitySymbols,
    IEntity,
    IEntityFragment,
    IOmniFieldSearchParams,
    IPagedSearchParams,
    IQueryParams,
    SafeAny,
    SearchStrategy,
    TableQueryParams,
} from '@pf/shared-common';
import { PojoMetadata, PojosMetadataMap } from '@automapper/pojos';

/**
 * Generates a symbol name based on entity name and type.
 * @param entityName - The name of the entity.
 * @param type - The type of the entity.
 * @returns A formatted symbol name string.
 */
function SymbolName(entityName: string, type: string) {
    return `${entityName}_${type}`;
}

/**
 * Creates entity symbols for mapping configurations.
 * @param entityName - The name of the entity.
 * @returns An object containing entity, createBody, dto, and searchParams symbols.
 */
export const entitySymbols = (entityName: string): EntitySymbols => {
    return {
        entity: SymbolName(entityName, 'entity'),
        createBody: SymbolName(entityName, 'createBody'),
        dto: SymbolName(entityName, 'dto'),
        searchParams: SymbolName(entityName, 'entitySearchParams'),
    };
};

export type ModelMetadata<T> = Record<keyof T, PojoMetadata | [PojoMetadata]>;
export type EntityMetadataExtras<TEntity> = Record<
    keyof Omit<TEntity, 'id' | 'isDeleted' | 'createdDate' | 'modifiedDate'>,
    PojoMetadata | [PojoMetadata]
>;
export type SearchParamsMetadataExtras<TSearchParams> = Record<
    keyof Omit<
        TSearchParams,
        'pageNumber' | 'pageSize' | 'orderBy' | 'doCount' | 'isDeleted'
    >,
    PojoMetadata | [PojoMetadata]
>;

/**
 * Adds metadata to the PojoMetadataMap.
 * @param symbol - The symbol associated with the metadata.
 * @param metadata - The metadata to be added.
 */
export const withMetadata = <T>(
    symbol: string | symbol,
    metadata: ModelMetadata<T>
) => {
    PojosMetadataMap.create<T>(symbol, metadata);
};

/**
 * Adds default entity metadata to the PojoMetadataMap.
 * @param symbol - The symbol associated with the entity metadata.
 * @param metadataExtras - Additional metadata for the entity.
 */
export const withDefaultEntityMetadata = <TEntity extends IEntity>(
    symbol: string | symbol,
    metadataExtras: EntityMetadataExtras<
        Omit<TEntity, 'id' | 'isDeleted' | 'createdDate' | 'modifiedDate'>
    >
): void => {
    withMetadata(symbol, {
        id: String,
        isDeleted: Boolean,
        createdDate: Date,
        modifiedDate: Date,
        ...metadataExtras,
    } as ModelMetadata<TEntity>);
};

/**
 * Adds default fragment metadata to the PojoMetadataMap.
 * @param symbol - The symbol associated with the fragment metadata.
 * @param metadataExtras - Additional metadata for the fragment.
 */
export const withDefaultFragmentMetadata = <
    TEntityFragment extends IEntityFragment,
>(
    symbol: string | symbol,
    metadataExtras: EntityMetadataExtras<Omit<TEntityFragment, 'id'>>
): void => {
    withMetadata(symbol, {
        id: String,
        ...metadataExtras,
    } as ModelMetadata<TEntityFragment>);
};

/**
 * Adds default search parameters metadata to the PojoMetadataMap.
 * @param symbol - The symbol associated with the search parameters metadata.
 * @param metadataExtras - Additional metadata for the search parameters.
 */
export const withDefaultSearchParamsMetadata = <
    TSearchParams extends IPagedSearchParams,
>(
    symbol: string | symbol,
    metadataExtras: Partial<SearchParamsMetadataExtras<TSearchParams>>
): void => {
    withMetadata(symbol, {
        pageNumber: Number,
        pageSize: Number,
        orderBy: [String],
        doCount: Boolean,
        isDeleted: Boolean,
        createdStartDate: String,
        createdEndDate: String,
        modifiedStartDate: String,
        modifiedEndDate: String,
        ...metadataExtras,
    } as ModelMetadata<TSearchParams>);
};

/**
 * Adds default omni search parameters metadata to the PojoMetadataMap.
 * @param symbol - The symbol associated with the omni search parameters metadata.
 * @param metadataExtras - Additional metadata for the omni search parameters.
 */
export const withDefaultOmniSearchParamsMetadata = <
    TSearchParams extends IPagedSearchParams,
>(
    symbol: string | symbol,
    metadataExtras: Partial<SearchParamsMetadataExtras<TSearchParams>>
): void => {
    withMetadata(symbol, {
        pageNumber: Number,
        pageSize: Number,
        orderBy: [String],
        doCount: Boolean,
        isDeleted: Boolean,
        searchStartsWithFields: [String],
        searchStartsWith: [String],
        searchContainsFields: [String],
        searchContains: [String],
        searchEqualsFields: [String],
        searchEquals: [String],
        ...metadataExtras,
    } as ModelMetadata<TSearchParams>);
};

/**
 * A converter to transform IQueryParams to a string array for sort order.
 */
export const sortOrderConverter: Converter<
    IQueryParams<IEntity>,
    string[] | null
> = {
    convert(source: IQueryParams<IEntity>) {
        if (!source.sortField) {
            return null;
        }
        const sortOrder = {
            ascend: 'asc',
            descend: 'desc',
        };
        return [
            `${source.sortField?.toString()}:${
                sortOrder[source.sortOrder || 'ascend']
            }`,
        ];
    },
};

/**
 * Provides default mapping configurations for search parameters.
 * @returns An array of mapping configurations.
 */
export const withDefaultSearchParamsMappingConfiguration = <
    TSearchParams extends IPagedSearchParams,
>(): MappingConfiguration<IQueryParams<IEntity>, TSearchParams>[] => {
    return [
        forMember(
            d => d.pageNumber,
            mapFrom(s => s.pageIndex)
        ),
        forMember(
            d => d.pageSize,
            mapFrom(s => s.pageSize)
        ),
        forMember(
            d => d.isDeleted,
            mapFrom(s =>
                (s as TableQueryParams<IEntity>).checkboxes?.isDeleted
                    ? (null as SafeAny)
                    : false
            )
        ),
        forMember(
            d => d.orderBy,
            convertUsing(sortOrderConverter, s => s)
        ),
        forMember(
            d => d.createdStartDate,
            mapFrom(
                s =>
                    (s.filters as SafeAny)?.['createdStartDate'] ||
                    (s.filters as SafeAny)?.['created']?.[0]
            )
        ),
        forMember(
            d => d.createdEndDate,
            mapFrom(
                s =>
                    (s.filters as SafeAny)?.['createdEndDate'] ||
                    (s.filters as SafeAny)?.['created']?.[1]
            )
        ),
        forMember(
            d => d.modifiedStartDate,
            mapFrom(
                s =>
                    (s.filters as SafeAny)?.['modifiedStartDate'] ||
                    (s.filters as SafeAny)?.['modified']?.[0]
            )
        ),
        forMember(
            d => d.modifiedEndDate,
            mapFrom(
                s =>
                    (s.filters as SafeAny)?.['modifiedEndDate'] ||
                    (s.filters as SafeAny)?.['modified']?.[1]
            )
        ),
    ];
};

/**
 * Provides default mapping configurations for omni search parameters.
 * @param mapper - A partial record of DTO to search fields mapping.
 * @returns An array of mapping configurations.
 */
export const withDefaultOmniSearchParamsMappingConfiguration = <
    TDto extends IEntity,
    TSearchField,
    TSearchParams extends IOmniFieldSearchParams,
>(
    mapper: Partial<Record<keyof TDto, TSearchField[]>>
): SafeAny[] => {
    return [
        ...(withDefaultSearchParamsMappingConfiguration<IPagedSearchParams>() as MappingConfiguration<
            IQueryParams<TDto>,
            SafeAny
        >[]),
        forMember(
            d => d.searchContainsFields,
            mapFrom(s => {
                return assignSearchFields(SearchStrategy.Contains, s, mapper);
            })
        ),
        forMember(
            d => d.searchContains,
            mapFrom(s =>
                s.searchStrategy === SearchStrategy.Contains
                    ? s.searchValue || null
                    : null
            )
        ),
        forMember(
            d => d.searchEqualsFields,
            mapFrom(s => {
                return assignSearchFields(SearchStrategy.Equals, s, mapper);
            })
        ),
        forMember(
            d => d.searchEquals,
            mapFrom(s =>
                s.searchStrategy === SearchStrategy.Equals
                    ? s.searchValue || null
                    : null
            )
        ),
        forMember(
            d => d.searchStartsWithFields,
            mapFrom(s => {
                return assignSearchFields(SearchStrategy.StartsWith, s, mapper);
            })
        ),
        forMember(
            d => d.searchStartsWith,
            mapFrom(s =>
                s.searchStrategy === SearchStrategy.StartsWith
                    ? s.searchValue || null
                    : null
            )
        ),
    ] as MappingConfiguration<IQueryParams<TDto>, TSearchParams>[];
};

/**
 * Assigns search fields based on the search strategy.
 * @param searchStrategy - The search strategy to use.
 * @param params - The query parameters.
 * @param mapper - A partial record of DTO to search fields mapping.
 * @returns An array of search fields or null.
 */
const assignSearchFields = <TDto extends IEntity, TSearchField>(
    searchStrategy: SearchStrategy,
    params: IQueryParams<TDto>,
    mapper: Partial<Record<keyof TDto, TSearchField[]>>
): Array<string> | null => {
    if (searchStrategy !== params.searchStrategy || !params.searchValue) {
        return null;
    }

    return Object.values(params.searchFields || {}).reduce((prev, curr) => {
        const searchFields = mapper[curr as keyof TDto] as TSearchField[];
        if (searchFields) {
            return [...prev, ...searchFields];
        }
        return prev;
    }, [] as TSearchField[]) as SafeAny as Array<string>;
};

/**
 * Provides mapping configuration for date range search filters.
 * @param dateRangeKey - The key for the date range (ie: created for createdStartDate and createdEndDate)
 * @returns An array of mapping configuration.
 */
export const withDateRangeSearchFilter = <TDto>(
    dateRangeKey: string
): MappingConfiguration<IQueryParams<TDto>>[] => {
    const startDateKey = dateRangeKey + 'StartDate';
    const endDateKey = dateRangeKey + 'EndDate';
    return [
        forMember(
            d => (d as SafeAny)[startDateKey],
            mapFrom(s => {
                return (s.filters as SafeAny)?.[dateRangeKey]?.[0];
            })
        ),
        forMember(
            d => (d as SafeAny)[endDateKey],
            mapFrom(s => {
                return (s.filters as SafeAny)?.[dateRangeKey]?.[1];
            })
        ),
    ];
};

/**
 * Provides mapping configuration for search filters.
 * @param dtoProp - The DTO property to map.
 * @param searchParamsProp - The search parameters property to map to (optional).
 * @returns A mapping configuration.
 */
export const withSearchFilter = <
    TDto,
    TSearchParams extends IPagedSearchParams,
>(
    dtoProp: keyof TDto,
    searchParamsProp?: keyof TSearchParams
): MappingConfiguration<IQueryParams<TDto>, TSearchParams> => {
    return forMember(
        d => (d as SafeAny)[searchParamsProp || dtoProp],
        mapFrom(s => {
            if (s.searchStrategy && s.searchStrategy >= 0) {
                return null;
            }
            return s.filters?.[dtoProp];
        })
    );
};

/**
 * Provides mapping configuration for array search filters.
 * @param dtoProp - The DTO property to map.
 * @param searchParamsProp - The search parameters property to map to (optional).
 * @returns A mapping configuration.
 */
export const withArraySearchFilter = <
    TDto,
    TSearchParams extends IPagedSearchParams,
>(
    dtoProp: keyof TDto,
    searchParamsProp?: keyof TSearchParams
): MappingConfiguration<IQueryParams<TDto>, TSearchParams> => {
    return forMember(
        d => (d as SafeAny)[searchParamsProp || dtoProp],
        mapFrom(s => {
            if (s.searchStrategy && s.searchStrategy >= 0) {
                return null;
            }
            return s.filters?.[dtoProp]
                ? typeof s.filters?.[dtoProp] === 'string'
                    ? s.filters?.[dtoProp].split(',')
                    : s.filters?.[dtoProp]
                : null;
        })
    );
};

/**
 * Provides mapping configuration for array search filters.
 * @param dtoProp - The DTO fragment property to map.
 * @returns A mapping configuration.
 */
export const withFragmentSearchFilter = <TDto>(
    dtoProp: keyof TDto
): MappingConfiguration<IQueryParams<TDto>> => {
    return forMember(
        d => d[dtoProp.toString() + 'Id'],
        mapFrom(s => {
            if (s.searchStrategy && s.searchStrategy >= 0) {
                return null;
            }
            return s.filters?.[dtoProp];
        })
    );
};

/**
 * Provides mapping configuration for array search filters.
 * @param dtoProp - The DTO fragment property to map.
 * @returns A mapping configuration.
 */
export const withFragmentArraySearchFilter = <TDto>(
    dtoProp: keyof TDto
): MappingConfiguration<IQueryParams<TDto>> => {
    return forMember(
        d => d[dtoProp.toString() + 'Id'],
        mapFrom(s => {
            if (s.searchStrategy && s.searchStrategy >= 0) {
                return null;
            }
            return s.filters?.[dtoProp]
                ? typeof s.filters?.[dtoProp] === 'string'
                    ? s.filters?.[dtoProp].split(',')
                    : s.filters?.[dtoProp]
                : null;
        })
    );
};

/**
 * Provides mapping configuration for search filters.
 * @param searchParamsProp - The search parameters property to map.
 * @returns A mapping configuration.
 */
export const withSearchFilterUnTyped = <
    TSearchParams extends IPagedSearchParams,
>(
    searchParamsProp?: keyof TSearchParams
): MappingConfiguration<IQueryParams<SafeAny>, TSearchParams> => {
    return forMember(
        d => (d as SafeAny)[searchParamsProp],
        mapFrom(s => {
            if (s.searchStrategy && s.searchStrategy >= 0) {
                return null;
            }
            return (s.filters as SafeAny)?.[searchParamsProp];
        })
    );
};

/**
 * Provides mapping configuration for search filters.
 * @param searchParamsProp - The search parameters property to map.
 * @returns A mapping configuration.
 */
export const withSearchFilterArrayUnTyped = <
    TSearchParams extends IPagedSearchParams,
>(
    searchParamsProp?: keyof TSearchParams
): MappingConfiguration<IQueryParams<SafeAny>, TSearchParams> => {
    return forMember(
        d => (d as SafeAny)[searchParamsProp],
        mapFrom(s => {
            if (s.searchStrategy && s.searchStrategy >= 0) {
                return null;
            }
            const filters = s.filters as SafeAny;
            return filters?.[searchParamsProp]
                ? typeof filters?.[searchParamsProp] === 'string'
                    ? filters?.[searchParamsProp].split(',')
                    : filters?.[searchParamsProp]
                : null;
        })
    );
};

export const withFragmentIdProp = <TSource extends IEntity, TDestination>(
    sourceProp: keyof TSource & string,
    destProp: (keyof TDestination & string) | null = null
): MappingConfiguration<TSource, TDestination> => {
    return forMember(
        d => (d as SafeAny)[destProp || `${sourceProp}Id`],
        mapFrom(s => ((s as SafeAny)[sourceProp] as IEntityFragment | null)?.id)
    );
};
